// Recebimentos › 🧾 Códigos da NF (15/10/2026) — reforma tributária.
//
// Desde 01/10/2026 a nota de serviço leva NBS, indicador da operação e
// classificação tributária. Aqui fica o PADRÃO de cada código de serviço, que a
// contabilidade confirma; a parcela segue o padrão e pode trocar na aba "Dados
// pra NF". Só se escolhe combinação que está na tabela oficial (o servidor
// confere de novo ao gravar). Régua: ./nfReforma.ts.
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, App as AntApp, Button, Input, Popconfirm, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { usePerms } from '../../hooks/usePerms';
import { CodigoNf, chaveCombo, rotuloSituacao } from './nfReforma';

const { Text } = Typography;

type Edicao = { nbs: string; ind_operacao: string; class_trib: string; observacao: string };

async function chamar(body: any) {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, body, { headers: { Authorization: token } });
  return r.data.body ? JSON.parse(r.data.body) : r.data;
}

export default function CodigosNfReforma() {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const podeEditar = pode('financeiro.contas_receber.editar') || pode('admin.permissoes.editar');
  const [codigos, setCodigos] = useState<CodigoNf[]>([]);
  const [versao, setVersao] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [edicao, setEdicao] = useState<Record<string, Edicao>>({});
  const [gravando, setGravando] = useState<string | null>(null);

  async function carregar() {
    setCarregando(true); setErro(null);
    try {
      const d = await chamar({ action: 'fin_listar_codigos_nf' });
      if (!Array.isArray(d?.codigos)) throw new Error(d?.error || 'resposta inesperada');
      setCodigos(d.codigos); setVersao(d.versao_anexo || '');
      setEdicao(Object.fromEntries(d.codigos.map((c: CodigoNf) => [c.codigo, {
        nbs: c.nbs || '', ind_operacao: c.ind_operacao || '', class_trib: c.class_trib || '', observacao: c.observacao || '',
      }])));
    } catch (e: any) {
      setErro(e?.response?.data?.error || e?.message || 'falha ao carregar');
    } finally { setCarregando(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregar(); }, []);

  const mudou = (c: CodigoNf) => {
    const e = edicao[c.codigo];
    return !!e && ((c.nbs || '') !== e.nbs || (c.ind_operacao || '') !== e.ind_operacao
      || (c.class_trib || '') !== e.class_trib || (c.observacao || '') !== e.observacao);
  };
  const editar = (codigo: string, campos: Partial<Edicao>) =>
    setEdicao(prev => ({ ...prev, [codigo]: { ...prev[codigo], ...campos } }));

  async function gravar(c: CodigoNf, confirmar: boolean) {
    const e = edicao[c.codigo];
    setGravando(c.codigo);
    try {
      // atualizado_em_visto: se outra pessoa gravou depois que a tela carregou,
      // o servidor recusa (409) em vez de confirmar valores que ninguém está vendo.
      const d = await chamar({ action: 'fin_salvar_codigo_nf', codigo: c.codigo, ...e, confirmar,
        atualizado_em_visto: (c as any).atualizado_em || null });
      if (d?.error) throw new Error(d.error);
      message.success(confirmar ? `Confirmação da contabilidade registrada para ${c.codigo}.` : `Código ${c.codigo} salvo.`);
      await carregar();
    } catch (err: any) {
      message.error(err?.response?.data?.error || err?.message || 'falha ao salvar');
      if (err?.response?.status === 409) await carregar();
    } finally { setGravando(null); }
  }

  if (carregando && !codigos.length) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Alert type="info" showIcon
        title="Códigos da reforma tributária na nota de serviço (IBS/CBS)"
        description={<>
          Desde 1º/10/2026 a nota leva <b>NBS</b>, <b>indicador da operação</b> e <b>classificação tributária</b>.
          Esta tabela guarda o padrão de cada código de serviço. A parcela segue o padrão e pode trocar na aba
          "🧾 Dados pra NF". Os valores iniciais são um rascunho de 15/10/2026: quando a contabilidade confirmar,
          clique em <b>Registrar confirmação</b>. Só aparecem combinações da tabela oficial
          {versao ? ` (${versao})` : ''} — fora dela a nota é recusada.
        </>} />
      {erro && <Alert type="error" showIcon title={`Não consegui carregar os códigos: ${erro}`}
        action={<Button size="small" onClick={carregar}>Tentar de novo</Button>} />}
      <Table<CodigoNf>
        rowKey="codigo" dataSource={codigos} pagination={false} size="middle" scroll={{ x: 1400 }}
        columns={[
          {
            title: 'Código de serviço', key: 'codigo', width: 220,
            render: (_, c) => <><b>{c.codigo}</b><div style={{ fontSize: 12, color: '#666' }}>{c.descricao}</div></>,
          },
          {
            // NBS e classificação em colunas próprias (a combinação inteira não cabia
            // num Select só — a classificação, que é o que mais importa, sumia).
            title: 'NBS', key: 'nbs', width: 290,
            render: (_, c) => {
              const e = edicao[c.codigo] || { nbs: '', ind_operacao: '', class_trib: '', observacao: '' };
              if (!c.tem_tabela) {
                return (
                  <Space orientation="vertical" style={{ width: '100%' }} size={4}>
                    <Input placeholder="NBS (ex.: 1.1403.10.00)" value={e.nbs} disabled={!podeEditar}
                      onChange={ev => editar(c.codigo, { nbs: ev.target.value })} />
                    <Input placeholder="Indicador da operação (ex.: 100301)" value={e.ind_operacao} disabled={!podeEditar}
                      onChange={ev => editar(c.codigo, { ind_operacao: ev.target.value })} />
                    <Text type="secondary" style={{ fontSize: 11.5 }}>Sem tabela oficial para este código — digite os três.</Text>
                  </Space>
                );
              }
              const nbsUnicas = Array.from(new Map(c.combinacoes.map(x => [x.nbs, x])).values());
              return (
                <>
                  <Select showSearch optionFilterProp="label" style={{ width: '100%' }} disabled={!podeEditar}
                    value={e.nbs || undefined} placeholder="Escolha a NBS"
                    onChange={(nbs: string) => {
                      // a classificação acompanha: fica a mesma se valer para a NBS nova; senão a primeira que vale
                      const validas = c.combinacoes.filter(x => x.nbs === nbs);
                      const fica = validas.find(x => x.class_trib === e.class_trib) || validas[0];
                      editar(c.codigo, { nbs, ind_operacao: fica?.ind_operacao || '', class_trib: fica?.class_trib || '' });
                    }}
                    options={nbsUnicas.map(x => ({ value: x.nbs, label: `${x.nbs} — ${x.nbs_descricao || ''}` }))} />
                  <div style={{ fontSize: 11.5, color: '#888', marginTop: 3 }}>
                    Indicador da operação: <span style={{ fontFamily: 'monospace' }}>{e.ind_operacao || '—'}</span>
                  </div>
                </>
              );
            },
          },
          {
            title: 'Classificação tributária', key: 'class', width: 290,
            render: (_, c) => {
              const e = edicao[c.codigo] || { nbs: '', ind_operacao: '', class_trib: '', observacao: '' };
              if (!c.tem_tabela) {
                return <Input placeholder="Classificação (ex.: 000001)" value={e.class_trib} disabled={!podeEditar}
                  onChange={ev => editar(c.codigo, { class_trib: ev.target.value })} />;
              }
              const validas = c.combinacoes.filter(x => x.nbs === e.nbs);
              return (
                <Select showSearch optionFilterProp="label" style={{ width: '100%' }} disabled={!podeEditar || !e.nbs}
                  value={chaveCombo(e.nbs, e.ind_operacao, e.class_trib) || undefined} placeholder="Escolha a classificação"
                  onChange={(v: string) => { const [nbs, ind, cls] = v.split('|'); editar(c.codigo, { nbs, ind_operacao: ind, class_trib: cls }); }}
                  options={validas.map(x => ({ value: chaveCombo(x.nbs, x.ind_operacao, x.class_trib), label: `${x.class_trib} — ${x.class_descricao || ''}` }))} />
              );
            },
          },
          {
            title: 'Situação', key: 'situacao', width: 190,
            render: (_, c) => c.situacao === 'confirmado'
              ? <>
                  <Tag color="green">Confirmado</Tag>
                  <div style={{ fontSize: 11.5, color: '#666', marginTop: 3 }}>{rotuloSituacao(c).replace(/^confirmado /, '')}</div>
                </>
              : <>
                  <Tag color="gold">Rascunho</Tag>
                  <div style={{ fontSize: 11.5, color: '#b06a00', marginTop: 3 }}>falta a contabilidade confirmar</div>
                </>,
          },
          {
            title: 'Observação', key: 'obs', width: 240,
            render: (_, c) => (
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} disabled={!podeEditar}
                value={edicao[c.codigo]?.observacao || ''} onChange={ev => editar(c.codigo, { observacao: ev.target.value })} />
            ),
          },
          {
            title: '', key: 'acoes', width: 190, fixed: 'right',
            render: (_, c) => podeEditar ? (
              <Space orientation="vertical" size={4}>
                <Button size="small" disabled={!mudou(c)} loading={gravando === c.codigo} onClick={() => gravar(c, false)}>
                  Salvar
                </Button>
                <Popconfirm
                  title="Registrar que a contabilidade confirmou estes códigos?"
                  description={`Fica gravado quem registrou e quando. Valores: ${[edicao[c.codigo]?.nbs, edicao[c.codigo]?.ind_operacao, edicao[c.codigo]?.class_trib].filter(Boolean).join(' · ') || '—'}`}
                  okText="Registrar" cancelText="Cancelar"
                  onConfirm={() => gravar(c, true)}
                  disabled={!edicao[c.codigo]?.nbs}>
                  <Button size="small" type="primary" disabled={!edicao[c.codigo]?.nbs} loading={gravando === c.codigo}>
                    Registrar confirmação
                  </Button>
                </Popconfirm>
              </Space>
            ) : null,
          },
        ]}
      />
    </div>
  );
}
